import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  CardComponent,
  ClaimDetail,
  Download,
  Layers,
  SvgTable,
  Table,
  ToggleGroup,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import DeepDiveConstants from '../../../core-utils/messages/DeepDiveConstants';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { ClaimDetailData } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { useStores } from '../../../stores';
import {
  formatClaimIDBoxView,
  formatClaimIDTableColumnView,
  formatClaimIDTableDataView,
  formatClaimIDTableDownloadColumnView,
} from '../../../core-utils/helper-utils/deepDiveHelper';
import { medicalHistoryClaimIDMessage } from '../../../core-utils/messages/riskPatientDetailsConstants';
import { CircularLoader } from '@HomaHealth/ui-component-library';
import { downloadCSV } from '../../../core-utils/helper-utils/downloadPdfHelper';

export interface ClaimDetailsModalProps {
  open: boolean;
  setOpen: any;
  handleOpenSnackBar: any;
  claimId: string;
  memberID: string;
}

const ClaimDetailsModal: React.FC<ClaimDetailsModalProps> = observer(
  ({ open, setOpen, claimId, memberID, handleOpenSnackBar }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },

      button: {
        padding: '0px',
      },
      tableHeader: {
        height: '48px',
        backgroundColor: patientAccessTheme.palette.grey[100],
        borderTop: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        '&:first-child': {
          paddingLeft: '32px',
        },
        textAlign: 'left',
      },
      tableRow: {
        height: '48px',
        padding: '0px',
        borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        '& td:first-child': {
          paddingLeft: '32px',
        },
        '& td': {
          padding: '1rem 0',
        },
      },
      overallGridStyles: {
        width: '100%',
        marginRight: '150px',
      },
      downloadButtonGrid: {
        marginTop: '20px',
        marginBottom: '20px',
      },
      downloadCSVGrid: {
        marginRight: '10px',
      },
      hide: {
        display: 'none',
      },
    });
    const { classes } = useStyles();
    const employerGroupStore = useStores().employerGroupStore;
    const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

    //States
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedTabName, setSelectedTabName] = useState<'left' | 'right'>(
      'left',
    );
    const [claimDetail, setClaimDetail] = useState<ClaimDetailData>();

    //Functions
    const handleClose = () => {
      setOpen(false);
    };

    const getTypeHeader = (subType: string) => {
      switch (subType) {
        case 'Pharmacy':
          return 'Rx Claim Details';
        case 'Facility':
          return 'Medical Facility Claim Details';
        case 'Professional':
          return 'Medical Professional Claim Details';
        default:
          return 'NA';
      }
    };

    //Apis
    const getClaimDetailAPI = async () => {
      setIsLoading(true);
      const url =
        properties.aiqBaseUrl +
        properties.membersBaseUrl +
        properties.claimDetail;
      const requestBody = {
        eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
        claim_id: claimId,
      };

      await axios
        .post(url, requestBody)
        .then((response) => {
          response?.data?.data;
          setClaimDetail(response?.data?.data);
          setIsLoading(false);
        })
        .catch((error) => {
          handleOpenSnackBar(error.response?.data?.error, 'error', 8000);
          setIsLoading(false);
        });
    };

    const handleDownloadCSV = async (
      downloadAnchorRef: React.RefObject<HTMLAnchorElement | null>,
    ) => {
      const url =
        properties.aiqBaseUrl +
        properties.membersBaseUrl +
        properties.claimDetailsCsv;
      const requestBody = {
        eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
        member_unique_id: memberID,
        claim_id: claimId,
      };

      downloadCSV(downloadAnchorRef, url, requestBody, claimId);
      handleOpenSnackBar(
        medicalHistoryClaimIDMessage.downloadCSVMessage,
        'info',
        5000,
      );
    };

    useEffect(() => {
      getClaimDetailAPI();
    }, []);

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          {isLoading ? (
            <CircularLoader />
          ) : (
            <CardComponent
              title={getTypeHeader(claimDetail?.subType!) + ' - ' + claimId}
              width={'900px'}
              height={'643px'}
              body={
                <>
                  {selectedTabName === 'left' ? (
                    <Grid style={{ marginTop: '20px' }}>
                      <ClaimDetail
                        data={formatClaimIDBoxView(claimDetail)}
                        cardWidth="875px"
                        cardHeight="600px"
                        claimRowData={formatClaimIDTableDataView(claimDetail)}
                        columnsValue={formatClaimIDTableColumnView(claimDetail)}
                      />
                      <Grid
                        style={{
                          display: 'flex',
                          justifyContent: 'right',
                          margin: '0px 24px 5px 0px',
                          position: 'absolute',
                          right: '0px',
                          bottom: '0px',
                        }}
                      >
                        <Button
                          label={
                            <Typography
                              textColor={
                                patientAccessTheme.palette.primary.main
                              }
                              fontSize="12px"
                              lineHeight="12px"
                              fontType={'medium'}
                            >
                              {DeepDiveConstants.CLOSE}
                            </Typography>
                          }
                          variant="outlined"
                          height="36px"
                          width="180px"
                          styleProps={{ marginRight: '25px' }}
                          borderRadius="4px"
                          className={classes.button}
                          onClick={() => {
                            handleClose();
                          }}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid>
                      <Grid style={{ height: '470px', marginTop: '20px' }}>
                        <Table
                          onHoverRowData={() => {}}
                          classNameForTableHeaderRowTop={classes.tableHeader}
                          classNameForTableDataRow={classes.tableRow}
                          classNameForTableHeader={classes.tableHeader}
                          columns={formatClaimIDTableDownloadColumnView(
                            claimDetail,
                          )}
                          data={claimDetail?.tableData}
                          controlledPageIndex={0}
                          overallGridStyles={classes.overallGridStyles}
                          isManualPagination={true}
                          tableHeight="470px"
                        />
                      </Grid>
                      <Grid
                        container
                        direction="row"
                        className={classes.downloadButtonGrid}
                        sx={{
                          justifyContent: 'flex-end',
                          alignItems: 'flex-end',
                        }}
                      >
                        <Grid className={classes.downloadCSVGrid}>
                          <a
                            className={classes.hide}
                            href="empty"
                            ref={downloadAnchorRef}
                          />
                          <Button
                            label={medicalHistoryClaimIDMessage.downloadCSV}
                            variant="outlined"
                            color="primary"
                            height="33px"
                            width="180px"
                            borderRadius="4px"
                            startIcon={<Download />}
                            onClick={() => handleDownloadCSV(downloadAnchorRef)}
                          />
                        </Grid>
                        {/* Need to implement in future so commenting now */}
                        {/* <Grid item>
                            <Button
                              label={medicalHistoryClaimIDMessage.downloadExcel}
                              variant="outlined"
                              color="primary"
                              height="33px"
                              width="180px"
                              borderRadius="4px"
                              startIcon={<Download />}
                              onClick={() =>
                                handleDownloadExcel(downloadAnchorRef)
                              }
                            />
                          </Grid> */}
                        <Grid style={{ marginLeft: '10px' }}>
                          <Button
                            label={
                              <Typography
                                textColor={
                                  patientAccessTheme.palette.primary.main
                                }
                                fontSize="12px"
                                lineHeight="12px"
                                fontType={'medium'}
                              >
                                {DeepDiveConstants.CLOSE}
                              </Typography>
                            }
                            variant="outlined"
                            height="36px"
                            width="180px"
                            styleProps={{ marginRight: '25px' }}
                            borderRadius="4px"
                            className={classes.button}
                            onClick={() => {
                              handleClose();
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </>
              }
              topRightItem={
                <ToggleGroup
                  leftValue={medicalHistoryClaimIDMessage.LEFT_VALUE}
                  rightValue={medicalHistoryClaimIDMessage.RIGHT_VALUE}
                  leftIcon={
                    <Layers style={{ height: '16px', width: '16px' }} />
                  }
                  rightIcon={
                    <SvgTable
                      style={{
                        height: '16px',
                        width: '16px',
                      }}
                    />
                  }
                  setSelectedToggle={setSelectedTabName}
                  showBothLabel={true}
                />
              }
            />
          )}
        </Modal>
      </>
    );
  },
);

export default ClaimDetailsModal;
